import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useEffect } from 'react';
import { Quote, ChevronLeft, ChevronRight } from 'lucide-react';

const Testimonials = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      quote: "Dr. Memon has been my PCP for a couple of years now and he's been extremely professional and thorough. I was pleased to find out that he added more services such as microneedling and hydrafacial. I've already had both done and the experience has been amazing. Definitely recommend",
      author: "Alea Mansoor"
    },
    {
      quote: "I have been a patient of Dr. Memon for a few years now and decided to get a hydrafacial done. Sumaya did my facial and she did such a great job. Definitely recommend!!",
      author: "Alisha Memon"
    },
    {
      quote: "Got my hydra-facial from Century Medical services and it was sincerely such a pleasant experience. I never do things like that but I might be spoiled by the treatment now! It's a safe, clean and friendly clinic. What more could you possibly ask for? Highly recommended!",
      author: "Hamza Khamisa"
    }
  ];

  // Responsive testimonials per page
  const getTestimonialsPerPage = () => {
    if (typeof window === 'undefined') return 3;
    if (window.innerWidth < 768) return 1; // Mobile: 1 per page
    if (window.innerWidth < 1024) return 2; // Tablet: 2 per page
    return 3; // Desktop: 3 per page
  };

  const [testimonialsPerPage, setTestimonialsPerPage] = useState(getTestimonialsPerPage());

  // Update testimonialsPerPage on resize
  useEffect(() => {
    const handleResize = () => {
      setTestimonialsPerPage(getTestimonialsPerPage());
      // Reset to first page if current page would be empty after resize
      const newTotalPages = Math.ceil(testimonials.length / getTestimonialsPerPage());
      if (currentIndex >= newTotalPages) {
        setCurrentIndex(0);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentIndex, testimonials.length]);

  const totalPages = Math.ceil(testimonials.length / testimonialsPerPage);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % totalPages);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);
  };

  const getCurrentTestimonials = () => {
    const start = currentIndex * testimonialsPerPage;
    return testimonials.slice(start, start + testimonialsPerPage);
  };

  return (
    <section id="testimonials" ref={ref} className="py-16 md:py-24 bg-cream" aria-label="Patient Testimonials and Reviews">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl font-semibold text-olive-darkest mb-3 md:mb-4">
            Patient Testimonials
          </h2>
          <div className="w-16 md:w-20 h-0.5 bg-gold mx-auto mb-4 md:mb-6" aria-hidden="true"></div>
          <p className="text-text-dark text-base sm:text-lg opacity-80 max-w-3xl mx-auto leading-relaxed px-4">
            Don't just take our word for it. Hear from our patients about their 
            experiences with Century Longevity & Aesthetics.
          </p>
        </motion.div>

        {/* Carousel Container */}
        <div className="relative" role="region" aria-label="Testimonials carousel">
          {/* Testimonials Grid */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8"
            >
              {getCurrentTestimonials().map((testimonial, index) => (
                <article
                  key={`${currentIndex}-${index}`}
                  className="bg-white p-6 sm:p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 relative"
                >
                  {/* Quote Icon */}
                  <div className="absolute top-4 sm:top-6 right-4 sm:right-6 text-gold opacity-20" aria-hidden="true">
                    <Quote className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12" />
                  </div>

                  {/* Testimonial Content */}
                  <div className="relative z-10">
                    <blockquote className="text-text-dark text-sm sm:text-base leading-relaxed mb-5 md:mb-6 italic">
                      "{testimonial.quote}"
                    </blockquote>

                    <footer className="border-t border-gold/20 pt-3 md:pt-4">
                      <cite className="font-heading text-lg sm:text-xl font-semibold text-olive-darkest mb-1 not-italic">
                        {testimonial.author}
                      </cite>
                    </footer>
                  </div>
                </article>
              ))}
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex justify-center items-center gap-3 md:gap-4 mt-10 md:mt-12">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={prevSlide}
              className="bg-olive-darkest text-cream p-2.5 md:p-3 rounded-full hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg active:scale-95 flex-shrink-0"
              aria-label="Previous testimonials"
            >
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
            </motion.button>

            {/* Dots Indicator */}
            <div className="flex gap-1.5 md:gap-2 overflow-x-auto max-w-[200px] sm:max-w-none">
              {Array.from({ length: totalPages }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-2.5 md:h-3 rounded-full transition-all duration-300 flex-shrink-0 ${
                    currentIndex === index 
                      ? 'bg-olive-darkest w-6 md:w-8' 
                      : 'bg-olive-darkest/30 hover:bg-olive-darkest/50 w-2.5 md:w-3'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={nextSlide}
              className="bg-olive-darkest text-cream p-2.5 md:p-3 rounded-full hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg active:scale-95 flex-shrink-0"
              aria-label="Next testimonials"
            >
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
            </motion.button>
          </div>

          {/* Swipe Hint for Mobile */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: isInView ? 1 : 0 }}
            transition={{ duration: 0.6, delay: 1 }}
            className="text-center text-xs sm:text-sm text-text-dark/50 mt-4 md:hidden"
          >
            Swipe or use arrows to see more
          </motion.p>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;